import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { PageShell } from "../components/layout/PageShell";
import { DataTable } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import { ClipboardList, FileText, ShieldCheck } from "lucide-react";
import {
  safeFormatDate,
  getPageSizeOptions,
  resolvePageSizeForViewMode,
} from "../../utils/formatters";
import {
  EntityCardGrid,
  MetaDates,
  StatTile,
  StatTiles,
  TitleTooltip,
} from "../components/EntityCardGrid";
import {
  searchAssessmentPlans,
  ASSESSMENT_PLAN_SORT_COLUMNS,
  type ApiAssessmentPlan,
} from "../../api/frameworksApi";

export function AssessmentReviewList() {
  const navigate = useNavigate();
  /* ── List state ──
   * Search, sorting, filtering and pagination are all resolved by the backend
   * via POST /assessment-plans/{page}/{size}; `plans` always holds exactly the
   * rows for the current page and is rendered as-is. */
  const [search, setSearch] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [viewMode, setViewMode] = useState<"card" | "table">("card");
  // Must be a valid option for the default view mode above ("card") — the
  // plain table default (10) isn't one of [12,24,48,96].
  const [pageSize, setPageSize] = useState(getPageSizeOptions("card")[0]);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const handleSort = (key: string) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(0);
  };

  const [plans, setPlans] = useState<ApiAssessmentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const fetchData = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    setError(null);
    try {
      const res = await searchAssessmentPlans(page, pageSize, {
        keyword: keyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: sortKey
          ? (ASSESSMENT_PLAN_SORT_COLUMNS[sortKey] ?? sortKey)
          : "",
        sortingOrder: sortKey ? sortDir : "",
        // This list only ever shows approved plans whose POA&M hasn't been
        // generated yet (the backend stamps `reviewCompletedAt` at that point).
        // With server-side paging that rule has to travel with the query, or
        // pages come back short.
        status: "approved",
        reviewCompleted: false,
      });
      setPlans(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower search) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (err) {
      console.error("Failed to load plans:", err);
      setError("Failed to load assessment plans.");
      setPlans([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [page, pageSize, keyword, sortKey, sortDir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  const displayTitle = (p: ApiAssessmentPlan) =>
    p.title || p.documentId || p.id;

  const resolveSspTitle = (p: ApiAssessmentPlan): string | null => {
    if (p.sspTitle) return p.sspTitle;
    if (!p.importSspHref) return null;
    return p.importSspHref.split("/").pop() ?? p.importSspHref;
  };

  const SortIcon = ({ col }: { col: string }) => {
    const active = sortKey === col;
    return (
      <span
        className="inline-flex flex-col items-center ml-2 select-none"
        style={{ fontSize: "7px", lineHeight: "7px" }}
      >
        <span
          className={`font-black ${active && sortDir === "asc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▲
        </span>
        <span
          className={`font-black ${active && sortDir === "desc" ? "text-[#0f172a] dark:text-white" : "text-[#94a3b8] dark:text-white/40"}`}
        >
          ▼
        </span>
      </span>
    );
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order, stage filter and page window. */
  const paged = plans;

  return (
    <PageShell className="flex flex-col flex-1 min-h-0 gap-6">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between">
        <div>
          <h1>Assessment Review</h1>
          <p className="text-[14px] text-[#94a3b8] dark:text-white/70">
            Review executed controls and provide assessor decisions
          </p>
        </div>
      </div>

      <EntityCardGrid<ApiAssessmentPlan>
        items={paged}
        loading={loading}
        getKey={(p) => p.id}
        getStatus={(p) => p.status ?? p.lifecycleStatus ?? "in_review"}
        hideStatusBadge
        onOpen={(p) => navigate(`/continuous-compliance/assessment/assessment-review/${p.id}`)}
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={(s) => {
          setPageSize(s);
          setPage(0);
        }}
        pageSizeOptions={[...getPageSizeOptions(viewMode)]}
        itemLabel="plans"
        emptyText={
          search
            ? "No plans match your search"
            : "No approved plans available for review"
        }
        viewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          setPageSize(resolvePageSizeForViewMode(pageSize, mode));
          setPage(0);
        }}
        toolbarLeft={
          <TableToolbar
            search={{
              value: search,
              onChange: setSearch,
              onSubmit: submitSearch,
              placeholder: "Search plans",
              ariaLabel: "Search plans",
            }}
          />
        }
        renderTitle={(p) => (
          <TitleTooltip text={displayTitle(p)}>
            <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
              {displayTitle(p)}
            </h3>
          </TitleTooltip>
        )}
        renderHeaderRight={() => (
          <ClipboardList className="size-4 text-indigo-500 dark:text-indigo-400" />
        )}
        renderBadges={(p) =>
          resolveSspTitle(p) && (
            <span className="inline-flex items-center gap-1 whitespace-nowrap max-w-full truncate px-1.5 py-0.5 rounded-md text-[10px] font-medium border bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
              <FileText className="size-3 flex-shrink-0" />
              <span className="truncate">{resolveSspTitle(p)}</span>
            </span>
          )
        }
        renderStats={(p) => (
          <StatTiles>
            <StatTile
              icon={<ShieldCheck className="size-3.5" />}
              accent="indigo"
              value={p.controlCount ?? 0}
              label="Controls"
            />
          </StatTiles>
        )}
        renderDates={(p) => (
          <MetaDates
            createdLabel="Start"
            updatedLabel="End"
            created={safeFormatDate(p.startDate)}
            updated={safeFormatDate(p.endDate)}
          />
        )}
        renderTable={(rows) => (
          <DataTable<ApiAssessmentPlan>
            columns={[
              {
                key: "no",
                header: "#",
                render: (_row, idx) => (
                  <span className="text-[#0f172a] dark:text-white/80 font-medium">
                    {page * pageSize + idx + 1}
                  </span>
                ),
              },
              {
                key: "name",
                align: "left",
                ellipsis: 2,
                tooltip: (p) => displayTitle(p) || "",
                header: (
                  <button
                    onClick={() => handleSort("name")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Plan Name
                    <SortIcon col="name" />
                  </button>
                ),
                render: (p) => (
                  <p className="font-medium text-[#0f172a] dark:text-white/80">
                    {displayTitle(p)}
                  </p>
                ),
              },
              {
                key: "ssp",
                align: "left",
                ellipsis: 2,
                tooltip: (p) => resolveSspTitle(p) || "",
                header: (
                  <button
                    onClick={() => handleSort("ssp")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Linked SSP
                    <SortIcon col="ssp" />
                  </button>
                ),
                render: (p) =>
                  resolveSspTitle(p) ? (
                    <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                      {resolveSspTitle(p)}
                    </span>
                  ) : (
                    <span className="text-[13px] font-medium text-[#0f172a] dark:text-white/80">
                      -
                    </span>
                  ),
              },
              {
                key: "controls",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("controls")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Controls
                    <SortIcon col="controls" />
                  </button>
                ),
                render: (p) => (
                  <span className="font-medium text-[#0f172a] dark:text-white/80">
                    {p.controlCount ?? p.reviewedControls?.length ?? 0} controls
                  </span>
                ),
              },
              {
                key: "startDate",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("startDate")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    Start Date
                    <SortIcon col="startDate" />
                  </button>
                ),
                render: (p) => (
                  <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                    {p.startDate
                      ? new Date(p.startDate).toLocaleDateString("en-GB")
                      : "-"}
                  </span>
                ),
              },
              {
                key: "endDate",
                align: "center",
                header: (
                  <button
                    onClick={() => handleSort("endDate")}
                    className="flex w-full items-center justify-center font-medium text-[#0f172a] dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                  >
                    End Date
                    <SortIcon col="endDate" />
                  </button>
                ),
                render: (p) => (
                  <span className="whitespace-nowrap font-medium text-[#0f172a] dark:text-white/80">
                    {p.endDate
                      ? new Date(p.endDate).toLocaleDateString("en-GB")
                      : "-"}
                  </span>
                ),
              },
            ]}
            data={rows}
            loading={loading}
            error={error}
            onRetry={fetchData}
            emptyText={
              search
                ? "No plans match your search"
                : "No approved plans available for review"
            }
            onRowClick={(p) => navigate(`/continuous-compliance/assessment/assessment-review/${p.id}`)}
            rowKey={(p) => p.id}
            itemLabel="plans"
          />
        )}
      />
    </PageShell>
  );
}
